import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';

import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

import {
  STEP_MESSAGES,
  buildSteps,
  getCustomizeStepSlotKind,
  getStepTitle,
  getVisibleCustomizeSteps,
  getVisibleStepDisplayIndex,
  isChooseYourShelvesCustomizeStep,
  isStepCompleteUtil,
} from '@amblique/king-living/app/hooks/use-customize-steps';
import {useAutoSelectHiddenSlots} from '@amblique/king-living/app/hooks/use-auto-select-hidden-slots';
import {useStepScrollHeader} from '@amblique/king-living/app/hooks/use-step-scroll-header';
import {GET_PRODUCTS_PARAMS, resolveCoverVariantViewDetailsProduct} from '@amblique/king-living/app/utils/api-utils';
import {
  applySiblingSlotMatchesByProductName,
  buildOptionSlotSelectionFromProduct,
  formatOptionProductSelectionName,
} from '@amblique/king-living/app/utils/customize-selections';
import {resolveCustomizeStepsGalleryState, resolveMostRecentCommittedOptionProductId} from '@amblique/king-living/app/utils/gallery-state-utils';

/**
 * Encapsulates all state, refs, and interaction handlers for CustomizeStepsContent.
 *
 * KINDW-1124 — controlled `activeSlot`:
 *   When `onActiveSlotChange` is provided, `activeSlot` is treated as a controlled
 *   value (read from `controlledActiveSlot`, written via `onActiveSlotChange`).
 *   This allows two `<CustomizeStepsContent>` instances (one inline rendering the
 *   steps view, one inside a Drawer rendering the picker view) to share the same
 *   active-slot state — required so the Configurable Master "Choose" interaction
 *   opens a real flyout overlay instead of swapping inline content (FSD §8.2,
 *   PDP-07: Customise Your Own). When `onActiveSlotChange` is omitted (Complex PDP
 *   and any other existing caller), the hook falls back to internal state and
 *   behaves exactly as before.
 */
export function useCustomizeStepsContent({
  controlledActiveSlot,
  coverGroups,
  coverVariantProduct: coverVariantProductFromParent,
  committedOptionProduct: committedOptionProductFromParent,
  drawerHeight,
  hasUserInteracted = false,
  isSimplePDP = false,
  onActiveSlotChange,
  onGalleryProductChange,
  onHeaderChange,
  onCoverPickerOpen,
  onSelect,
  onSlotProductPreview,
  onSubViewChange,
  onUserInteract,
  onViewDetails,
  product,
  selections,
}) {
  const intl = useIntl();
  const isMobile = useBreakpointValue({base: true, lg: false});

  const STEPS = useMemo(
    () => buildSteps(product, {isSimplePDP}),
    [product, isSimplePDP]
  );

  const [internalActiveSlot, setInternalActiveSlot] = useState(null);
  const isActiveSlotControlled = typeof onActiveSlotChange === 'function';
  const activeSlot = isActiveSlotControlled ?
    (controlledActiveSlot ?? null) :
    internalActiveSlot;
  // Keep the setter identity-stable (matches useState setter semantics) so the
  // many useCallback/useEffect deps below that DON'T include it remain correct.
  // The latest controlled inputs are read from a ref at call time.
  const activeSlotControlRef = useRef({
    activeSlot,
    isControlled: isActiveSlotControlled,
    onChange: onActiveSlotChange,
  });

  activeSlotControlRef.current = {
    activeSlot,
    isControlled: isActiveSlotControlled,
    onChange: onActiveSlotChange,
  };
  const setActiveSlot = useCallback((next) => {
    const {activeSlot: current, isControlled, onChange} = activeSlotControlRef.current;

    if (isControlled) {
      const value = typeof next === 'function' ? next(current) : next;

      onChange(value);
    } else {
      setInternalActiveSlot(next);
    }
  }, []);
  const [activeStepId, setActiveStepId] = useState(null);
  const [galleryPreviewProduct, setGalleryPreviewProduct] = useState(null);
  const [liveMessage, setLiveMessage] = useState('');

  const isUserScrollingRef = useRef(false);
  const lastCompletedSlotRef = useRef(null);
  // Last product committed with Next per "${stepId}-${slotIndex}"; Back reverts preview to this.
  const confirmedProductPerStep = useRef({});
  const scrollBodyRef = useRef(null);
  const slotButtonRefs = useRef({});
  const stepRefs = useRef({});
  // Carries the just-completed step from handleSlotNext into the post-render scroll effect.
  // A setTimeout inside the effect gives iOS Safari time to fully paint the DOM before scrolling.
  const pendingScrollStepRef = useRef(null);
  // Latest committed cover SCAPI product for View Details cache reuse.
  const coverVariantProductRef = useRef(null);

  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const inventoryIds = useShopperInventoryIds();

  // Hidden slot auto-fill must not mark hasUserInteracted — use the silent variant
  // so the Next button stays hidden until the user makes an explicit choice.
  const onSelectSilent = useCallback(
    (stepId, value) => onSelect(stepId, value, true),
    [onSelect]
  );

  useAutoSelectHiddenSlots(STEPS, selections, onSelectSilent);

  const isStepComplete = useCallback(
    (stepId) => isStepCompleteUtil(stepId, selections, STEPS),
    [selections, STEPS]
  );

  const handleBack = useCallback(() => {
    if (activeSlot) {
      const key = `${activeSlot.stepId}-${activeSlot.slotIndex}`;

      setGalleryPreviewProduct(
        activeSlot.stepId === 'cover' ? null : (confirmedProductPerStep.current[key] ?? null)
      );
    }
    setActiveSlot(null);
    onHeaderChange?.(null);
  }, [activeSlot, onHeaderChange]);

  const handleStepInteraction = useCallback((stepId) => {
    const step = STEPS.find((s) => s.id === stepId);

    if (!step) return;

    setActiveStepId(stepId);
    onHeaderChange?.({
      chooseYourShelvesStep: isChooseYourShelvesCustomizeStep(step),
      customizeStepKind: getCustomizeStepSlotKind(step),
      stepIndex: getVisibleStepDisplayIndex(stepId, STEPS),
      title: getStepTitle(step, intl),
      totalSteps: getVisibleCustomizeSteps(STEPS).length,
    });
  }, [STEPS, onHeaderChange, intl]);

  const handleSlotChoose = useCallback((stepId, slotIndex) => {
    handleStepInteraction(stepId);
    onUserInteract?.();
    const restored =
      stepId === 'cover' ? null : (confirmedProductPerStep.current[`${stepId}-${slotIndex}`] ?? null);

    setGalleryPreviewProduct(restored);
    // Capture whether a selection already existed BEFORE opening the picker.
    // For cover, the selection is committed immediately on pick (not deferred to Next),
    // so we must snapshot this here rather than reading selections inside handleSlotNext.
    const hadExistingSelection =
      stepId === 'cover' ? !!selections?.cover?.productId : !!selections?.[stepId]?.[slotIndex]?.productId;

    setActiveSlot({slotIndex, stepId, hadExistingSelection});

    if (stepId === 'cover' && isSimplePDP) {
      onCoverPickerOpen?.();
    }
  }, [handleStepInteraction, isSimplePDP, onCoverPickerOpen, onUserInteract, selections]);

  const handleSlotProductPreview = useCallback((previewProduct) => {
    setGalleryPreviewProduct(previewProduct);
    onSlotProductPreview?.(previewProduct);
  }, [onSlotProductPreview]);

  const handleCoverVariantViewDetails = useCallback(async (variant, viewOptions = {}) => {
    const {coverItemType} = viewOptions;
    const detailProduct = await resolveCoverVariantViewDetailsProduct({
      variant,
      masterProduct: product,
      selectedCoverProductId: selections?.cover?.productId ?? null,
      cachedCoverVariantProduct: coverVariantProductRef.current,
      shopperProducts: api.shopperProducts,
      getTokenWhenReady,
    });

    onViewDetails?.(
      detailProduct,
      coverItemType != null && coverItemType !== '' ? {coverItemType} : {}
    );
  }, [
    api.shopperProducts,
    getTokenWhenReady,
    inventoryIds,
    onViewDetails,
    product,
    selections?.cover?.productId
  ]);

  /** Option-slot "View details" — shelves-only UI when step is "Choose your shelves", not other shelf rows. */
  const handleOptionProductViewDetails = useCallback(
    async (pickerProduct) => {
      if (!activeSlot || activeSlot.stepId === 'cover') {
        onViewDetails?.(pickerProduct);

        return;
      }

      const step = STEPS.find((s) => s.id === activeSlot.stepId);
      const canonicalShelves = intl.formatMessage(STEP_MESSAGES.shelves);
      const stepTitle = step ? getStepTitle(step, intl).trim() : '';
      const titleBase = stepTitle.replace(/\s*:\s*\d+\s*\/\s*\d+\s*$/, '').trim();
      const shelvesDetailUi =
        isChooseYourShelvesCustomizeStep(step) ||
        titleBase === canonicalShelves.trim() ||
        stepTitle === canonicalShelves.trim();

      const clickedId = pickerProduct?.id ?? pickerProduct?.productId ?? null;
      let detailProduct = pickerProduct;

      if (clickedId) {
        try {
          const token = await getTokenWhenReady();
          const res = await api.shopperProducts.getProducts({
            headers: {Authorization: `Bearer ${token}`},
            parameters: {
              ids: clickedId,
              ...GET_PRODUCTS_PARAMS,
              ...(inventoryIds ? {inventoryIds} : {}),
            },
          });

          detailProduct = res?.data?.[0] ?? pickerProduct;
        } catch {
          detailProduct = pickerProduct;
        }
      }

      onViewDetails?.(detailProduct, {shelvesDetailUi});
    },
    [
      activeSlot,
      STEPS,
      api.shopperProducts,
      getTokenWhenReady,
      inventoryIds,
      intl,
      onViewDetails
    ]
  );

  const handleSlotNext = useCallback(() => {
    if (!activeSlot) return;

    const step = STEPS.find((s) => s.id === activeSlot.stepId);
    const slotKey = `${activeSlot.stepId}-${activeSlot.slotIndex}`;

    if (activeSlot.stepId !== 'cover') {
      if (!step?.container) return;

      const {container} = step;
      const value = container.values?.[activeSlot.slotIndex];

      if (!value) return;

      const row = [...(selections?.[activeSlot.stepId] || [])];
      const existing = row[activeSlot.slotIndex];

      if (galleryPreviewProduct) {
        row[activeSlot.slotIndex] = buildOptionSlotSelectionFromProduct(
          galleryPreviewProduct,
          activeSlot.slotIndex,
          value,
          container
        );

        // After confirming a slot, propagate to empty sibling slots with a matching
        // product name (e.g. shelves — FSD: both empty → one pick fills both).
        // Skips siblings that already have a selection. Page-load preselect stays
        // off on Simple PDP via mergePreselectedSelectionsFromProduct disablePreselect.
        const slotIdsBeforeSync = row.map((slot) => slot?.productId ?? null);
        const syncedRow = applySiblingSlotMatchesByProductName(
          container,
          row,
          activeSlot.slotIndex,
          galleryPreviewProduct
        );

        onSelect(container.containerId, syncedRow);

        syncedRow.forEach((sel, j) => {
          if (j === activeSlot.slotIndex) return;
          if (slotIdsBeforeSync[j] != null || !sel?.productId) return;

          confirmedProductPerStep.current[`${activeSlot.stepId}-${j}`] = {
            id: sel.productId,
            imageGroups: [],
            name: sel.name,
          };
        });
      } else if (!existing?.productId) {
        return;
      }
    }

    isUserScrollingRef.current = false;
    lastCompletedSlotRef.current = {slotIndex: activeSlot.slotIndex, stepId: activeSlot.stepId};

    if (galleryPreviewProduct) {
      confirmedProductPerStep.current[slotKey] = galleryPreviewProduct;
    }

    const announceName =
      activeSlot.stepId === 'cover' ?
        selections?.cover?.name :
        (formatOptionProductSelectionName(galleryPreviewProduct) ||
          selections?.[activeSlot.stepId]?.[activeSlot.slotIndex]?.name);

    if (announceName) {
      const stepLabel = step ? getStepTitle(step, intl) : activeSlot.stepId;

      setLiveMessage(`${stepLabel} selected: ${announceName}.`);
    }

    // Always advance to the next incomplete step after Done — including when the
    // user re-selects ("Change"). KINDW-1244 feature: confirming an option flows
    // straight into the next option container.
    pendingScrollStepRef.current = step;
    setActiveSlot(null);
  }, [activeSlot, galleryPreviewProduct, STEPS, selections, onSelect, intl, isSimplePDP]);

  const handleMainNext = useCallback(() => {
    const nextStep = STEPS.find((s) => !isStepComplete(s.id));

    if (nextStep && stepRefs.current[nextStep.id]) {
      stepRefs.current[nextStep.id].scrollIntoView({behavior: 'smooth', block: 'start'});
      setActiveStepId(nextStep.id);
      onHeaderChange?.({
        chooseYourShelvesStep: isChooseYourShelvesCustomizeStep(nextStep),
        customizeStepKind: getCustomizeStepSlotKind(nextStep),
        stepIndex: getVisibleStepDisplayIndex(nextStep.id, STEPS),
        title: getStepTitle(nextStep, intl),
        totalSteps: getVisibleCustomizeSteps(STEPS).length,
      });
    }
  }, [STEPS, isStepComplete, onHeaderChange, intl]);

  // Update header when entering sub-view.
  //
  // Simple PDP (FSD §SECTION-PDP-SIMPLE-08): picker header "Choose {typeName}", no step counter.
  // Complex PDP (KINDW-1331): same slot title when typeName is set; step counter stays visible.
  useEffect(() => {
    onSubViewChange?.(activeSlot !== null);
    if (activeSlot !== null) {
      setActiveStepId(null);
      const step = STEPS.find((s) => s.id === activeSlot.stepId);

      if (step) {
        const isOptionSlot = activeSlot.stepId !== 'cover';
        const slotValue = isOptionSlot ?
          step.container?.values?.[activeSlot.slotIndex] :
          null;
        const slotLabel = slotValue?.typeName?.trim() || null;
        const useSimpleSlotTitle = isSimplePDP && isOptionSlot && slotLabel;
        const useComplexSlotPickerTitle = !isSimplePDP && isOptionSlot && slotLabel;
        const useSlotPickerTitle = useSimpleSlotTitle || useComplexSlotPickerTitle;
        const title = useSlotPickerTitle ?
          intl.formatMessage(
            {
              defaultMessage: 'Choose {slotName}',
              id: 'customize_panel.picker.header_title',
            },
            {slotName: slotLabel}
          ) :
          getStepTitle(step, intl);

        onHeaderChange?.({
          chooseYourShelvesStep: isChooseYourShelvesCustomizeStep(step),
          customizeStepKind: getCustomizeStepSlotKind(step),
          onBack: handleBack,
          stepIndex: useSimpleSlotTitle ? null : getVisibleStepDisplayIndex(activeSlot.stepId, STEPS),
          title,
          totalSteps: useSimpleSlotTitle ? null : getVisibleCustomizeSteps(STEPS).length,
        });
      }
    }
  }, [activeSlot, onSubViewChange, STEPS, onHeaderChange, handleBack, intl, isSimplePDP]);

  // KINDW-1124 — cleanup when leaving a sub-view by ANY path.
  // handleBack performs preview reset + header clear inline; this effect mirrors
  // that behaviour for transitions caused by external setters (e.g. the
  // Configurable Master flyout drawer overlay click → CustomizeStepsInline
  // setActiveSlot(null)). prevActiveSlotRef ensures we only restore on the
  // null-transition, never on initial mount where activeSlot starts at null.
  const prevActiveSlotRef = useRef(null);

  useEffect(() => {
    const prev = prevActiveSlotRef.current;

    prevActiveSlotRef.current = activeSlot;

    if (activeSlot !== null || prev === null) return;

    const key = `${prev.stepId}-${prev.slotIndex}`;

    setGalleryPreviewProduct(
      prev.stepId === 'cover' ? null : (confirmedProductPerStep.current[key] ?? null)
    );
    onHeaderChange?.(null);
  }, [activeSlot, onHeaderChange]);

  // Derive cover variation values from the current selections so the gallery can
  // pass them to mergeProductWithVariantCylindo and resolve the correct Cylindo
  // params (c_cylindoId / c_cylindoFeature) for the selected cover variant.
  const coverVariationValues = selections?.cover?.variationValues ?? null;

  const selectedCoverId = selections?.cover?.productId ?? null;
  const isCoverPickerOpen = activeSlot?.stepId === 'cover';

  const shouldFetchCoverVariant =
    coverVariantProductFromParent === undefined && Boolean(selectedCoverId);

  const {data: variantProductResult} = useProducts(
    {parameters: {ids: selectedCoverId ?? '', ...GET_PRODUCTS_PARAMS}},
    {enabled: shouldFetchCoverVariant, keepPreviousData: true}
  );
  const coverVariantProductFromFetch = selectedCoverId ?
    (variantProductResult?.data?.[0] ?? null) :
    null;
  const coverVariantProduct = coverVariantProductFromParent !== undefined ?
    (coverVariantProductFromParent ?? null) :
    coverVariantProductFromFetch;

  coverVariantProductRef.current = coverVariantProduct;

  // Simple PDP fallback: derive the most-recently-CHANGED non-cover option productId
  // from the committed slots' `selectedAt` recency marker (stamped by
  // buildOptionSlotSelectionFromProduct). Required because committed selections survive
  // a page refresh (sessionStorage in CustomizeJourneyContext) but `galleryPreviewProduct`
  // does NOT — it's local state inside this hook. Without this fallback, on refresh
  // `viewDetailsProduct` is null for products with no cover step (e.g. ETO Compact Desk
  // has only `finish` + `extra` containers), so the LHS configuration image disappears
  // until the user clicks an option (which sets `galleryPreviewProduct` again).
  //
  // Recency (not catalog order) so the ViewDetails below the hero reflects the option the
  // shopper most recently updated — matching the hero image/video. Ties (equal/absent
  // markers) fall back to the LAST slot in STEP order via `>=`, preserving the previous
  // reverse-walk (bottom-most) behavior. See `useEffect` below for the propagation effect.
  const mostRecentCommittedOptionProductId = useMemo(() => {
    if (!isSimplePDP || !STEPS?.length || !selections) return null;

    return resolveMostRecentCommittedOptionProductId(selections, STEPS);
  }, [isSimplePDP, STEPS, selections]);

  const shouldFetchCommittedOption =
    committedOptionProductFromParent === undefined && Boolean(mostRecentCommittedOptionProductId);

  const {data: mostRecentCommittedOptionProductResult} = useProducts(
    {parameters: {ids: mostRecentCommittedOptionProductId ?? '', ...GET_PRODUCTS_PARAMS}},
    {enabled: shouldFetchCommittedOption}
  );
  const mostRecentCommittedOptionProductFromFetch = mostRecentCommittedOptionProductId ?
    (mostRecentCommittedOptionProductResult?.data?.[0] ?? null) :
    null;
  const mostRecentCommittedOptionProduct = committedOptionProductFromParent !== undefined ?
    (committedOptionProductFromParent ?? null) :
    mostRecentCommittedOptionProductFromFetch;

  const {mirrorGalleryProduct, viewDetailsProduct} = resolveCustomizeStepsGalleryState({
    activeSlot,
    coverVariantProduct,
    draftSelections: selections,
    galleryPreviewProduct,
    hasUserInteracted,
    isCoverPickerOpen,
    isSimplePDP,
    masterProduct: product,
    mostRecentCommittedOptionProduct,
    selectedCoverId,
  });

  useEffect(() => {
    if (isSimplePDP) return;

    onGalleryProductChange?.({
      product: mirrorGalleryProduct,
      selectedVariationAttributes: coverVariationValues ?? {},
      viewDetailsProduct,
    });
  }, [
    isSimplePDP,
    mirrorGalleryProduct,
    onGalleryProductChange,
    coverVariationValues,
    viewDetailsProduct
  ]);

  useStepScrollHeader({steps: STEPS, activeSlot, stepRefs, scrollBodyRef, onHeaderChange, setActiveStepId});

  // Reset scroll only when ENTERING a sub-view.
  // When leaving (activeSlot → null), StepsMainView mounts fresh so scrollTop is
  // already 0 — an explicit reset here would fight with the scroll-to-step effect below.
  useEffect(() => {
    if (activeSlot !== null && scrollBodyRef.current) {
      scrollBodyRef.current.scrollTop = 0;
    }
  }, [activeSlot]);

  // Scroll to the next incomplete step after handleSlotNext returns to the main view.
  // Simple PDP mobile keeps window scroll (inline drawer); skip auto-advance like desktop flyout.
  useEffect(() => {
    if (activeSlot !== null || !pendingScrollStepRef.current) return;

    const step = pendingScrollStepRef.current;

    pendingScrollStepRef.current = null;

    if (isSimplePDP && isMobile) {
      return;
    }

    const timer = setTimeout(() => {
      if (isUserScrollingRef.current) return;

      let targetStep;

      if (step?.layoutType === 'slot-cards' && step.container?.values) {
        const stepSelections = selections?.[step.id] || [];
        const hasIncompleteSlots = step.container.values.some(
          (v, idx) => v.isRequired && stepSelections[idx] == null
        );

        if (hasIncompleteSlots) targetStep = step;
      }

      if (!targetStep) targetStep = STEPS.find((s) => !isStepComplete(s.id));

      const el = targetStep ? stepRefs.current[targetStep.id] : null;

      if (!el || !scrollBodyRef.current) return;

      el.scrollIntoView({behavior: 'smooth', block: 'start'});

      setActiveStepId(targetStep.id);
      onHeaderChange?.({
        chooseYourShelvesStep: isChooseYourShelvesCustomizeStep(targetStep),
        customizeStepKind: getCustomizeStepSlotKind(targetStep),
        stepIndex: getVisibleStepDisplayIndex(targetStep.id, STEPS),
        title: getStepTitle(targetStep, intl),
        totalSteps: getVisibleCustomizeSteps(STEPS).length,
      });
    }, 50);

    return () => clearTimeout(timer);
  }, [activeSlot, selections, STEPS, isStepComplete, onHeaderChange, intl, isSimplePDP, isMobile]);

  // Pause auto-scroll when user manually scrolls
  useEffect(() => {
    const el = scrollBodyRef.current;

    if (!el) return;
    const handler = () => {
      isUserScrollingRef.current = true;
    };

    el.addEventListener('scroll', handler, {passive: true});

    return () => el.removeEventListener('scroll', handler);
  }, []);

  // Focus management: leaving picker → focus "Choose" button of completed slot
  useEffect(() => {
    if (!activeSlot && lastCompletedSlotRef.current) {
      const {slotIndex, stepId} = lastCompletedSlotRef.current;
      const el = slotButtonRefs.current[`${stepId}-${slotIndex}`];

      if (el) {
        try {
          el.focus({preventScroll: true});
        } catch {
          el.focus();
        }
      }
      lastCompletedSlotRef.current = null;
    }
  }, [activeSlot]);

  const priceProcessed = useMemo(() => {
    try {
      const raw = product?.c_priceProcessed;

      return raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {};
    } catch {
      return {};
    }
  }, [product]);

  const hasCoverData = coverGroups && coverGroups.length > 0;

  /** Panel chrome subtracted from mobile drawer height (header + sticky footer + inline gallery). */
  const MOBILE_CUSTOMIZE_GALLERY_HEADER_PX = 240;
  const bodyHeight = drawerHeight ?
    Math.max(
      0,
      drawerHeight - 64 - 80 - (isMobile ? MOBILE_CUSTOMIZE_GALLERY_HEADER_PX : 0)
    ) :
    undefined;
  const activeStepData = activeSlot ? STEPS.find((s) => s.id === activeSlot.stepId) : null;

  return {
    STEPS,
    activeSlot,
    activeStepData,
    activeStepId,
    bodyHeight,
    currency: product?.currency ?? 'USD',
    galleryPreviewProduct,
    hasCoverData,
    isMobile,
    isStepComplete,
    liveMessage,
    masterMinPrice: priceProcessed?.min ?? priceProcessed?.salePrice ?? null,
    scrollBodyRef,
    slotButtonRefs,
    stepRefs,
    handleBack,
    handleCoverVariantViewDetails,
    handleMainNext,
    handleOptionProductViewDetails,
    handleSlotChoose,
    handleSlotNext,
    handleSlotProductPreview,
    handleStepInteraction,
  };
}
